import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DEFAULT_ANIMATION_SPEED = 300;

/** Pointer travel, in pixels, before a press on the tablist counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5;

/**
 * Per-tab overrides a block can set, as `panel attribute -> [custom property, unit]`. The block
 * writes these onto its panel; {@link TabsSectionComponent} copies them onto the tab button.
 *
 * Keep this list narrow. Anything here is set inline on the button and so silently outranks the
 * section's own value, leaving that setting looking broken in the editor — colors were tried here
 * and pulled back out for exactly that reason.
 * @type {Record<string, [string, string]>}
 */
const TAB_STYLE_OVERRIDES = {
  'data-tab-radius': ['--tab-radius', 'px'],
};

/**
 * @typedef {Object} TabsSectionRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement[]} [tabLabels]
 * @property {HTMLElement[]} [tabMedia]
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/**
 * Tabbed content section: one tab button per `_tab` block, each controlling the
 * visibility of the panel that block renders. Also drives the Tabbed collections and Collection
 * preview sections; the latter sets `data-select-on-hover`, so a mouse resting on a tab selects it.
 *
 * @extends {Component<TabsSectionRefs>}
 */
class TabsSectionComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Animation | null} */
  #fade = null;
  /** @type {{ pointerId: number, startX: number, scrollLeft: number, moved: boolean } | null} */
  #drag = null;
  #swallowClick = false;
  /** The pointer behind the latest press, so a tap can be told apart from a mouse click. */
  #lastPointerType = '';
  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.#videoObserver = new IntersectionObserver(this.#handleVideoVisible);

    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    if (this.#selectsOnHover) this.addEventListener('pointerover', this.#handlePointerOver, opts);
    // Images and links inside a tab are natively draggable, which would start a ghost drag
    // instead of scrolling the strip.
    this.addEventListener('dragstart', this.#preventNativeDrag, opts);
    this.addEventListener('click', this.#swallowClickAfterDrag, { ...opts, capture: true });

    // Autoplay is asynchronous, so a video in a closed panel can start after a pass of
    // #syncPanelVideos has already run. Catching it as it starts is what keeps it from playing on
    // unseen. Media events don't bubble, so this listens in the capture phase.
    this.addEventListener('play', this.#pauseHiddenPlayback, { ...opts, capture: true });

    // Reveal a hidden panel when its block is selected in the theme editor.
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, opts);

    this.#syncLabels();
    this.#syncMedia();
    this.#syncTabStyles();
    this.#syncNav();
    this.#syncPanelVideos();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncLabels();
    this.#syncMedia();
    this.#syncTabStyles();
    this.#syncNav();
    this.#syncPanelVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#fade?.cancel();
    this.#fade = null;
    window.removeEventListener('pointermove', this.#handleDragMove);
    window.removeEventListener('pointerup', this.#endDrag);
    window.removeEventListener('pointercancel', this.#endDrag);
    this.#drag = null;
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
  }

  /** @returns {boolean} Whether panel changes should cross-fade. */
  get #animates() {
    return this.hasAttribute('data-animate') && !prefersReducedMotion();
  }

  /** @returns {number} The full fade duration in milliseconds. */
  get #duration() {
    return Number(this.dataset.animationSpeed) || DEFAULT_ANIMATION_SPEED;
  }

  /** @returns {boolean} Whether a mouse resting on a tab selects it, as in the Collection preview section. */
  get #selectsOnHover() {
    return this.hasAttribute('data-select-on-hover');
  }

  /**
   * Selects the tab under a mouse. Touch is left to the click: a tap fires `pointerover` too, and
   * selecting there would leave the tap's click nothing to tell apart.
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    const index = tab instanceof HTMLElement ? (this.refs.tabs ?? []).indexOf(tab) : -1;
    // Moving across a tab's own children fires this again; leave a fade already headed there alone.
    if (index === -1 || tab?.getAttribute('aria-selected') === 'true') return;

    this.#activate(index);
  };

  /**
   * Activates a tab. Bound declaratively via `on:click="/select/{index}"`.
   *
   * A tab can be a link (Collection tabs' Link tabs to collections setting). A click on it goes
   * straight to its collection page, so the tab isn't switched here; only a drag of the tab row
   * is kept from counting as a click.
   * @param {number} index - The index of the tab to activate.
   * @param {Event} [event] - The click.
   */
  select = (index, event) => {
    const tab = this.refs.tabs?.[index];
    const isLink = tab instanceof HTMLAnchorElement && tab.hasAttribute('href');

    // The theme binds `on:click` on document, which captures ahead of this component's own
    // handler, so the drag guard has to live here to beat it.
    if (this.#swallowClick) {
      this.#swallowClick = false;
      if (isLink) event?.preventDefault();
      return;
    }

    // The link is followed as it is, modified clicks included — unless the tabs select on hover
    // and this is a tap on one that isn't showing: touch has no hover, so the first tap shows the
    // tab and the second follows the link.
    if (isLink) {
      const tapped = this.#lastPointerType === 'touch' || this.#lastPointerType === 'pen';
      if (this.#selectsOnHover && tapped && tab.getAttribute('aria-selected') !== 'true') {
        event?.preventDefault();
        this.#activate(index);
      }
      return;
    }

    this.#activate(index);
  };

  /** Activates the tab before the current one. Bound via `on:click="/previous"`. */
  previous = () => {
    this.#step(-1);
  };

  /** Activates the tab after the current one. Bound via `on:click="/next"`. */
  next = () => {
    this.#step(1);
  };

  /**
   * Moves the selection by one tab and brings it into view. Stops at both ends rather than
   * wrapping, so the arrows' disabled state matches what a click will do.
   * @param {number} direction - `-1` for the previous tab, `1` for the next.
   */
  #step(direction) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const index = Math.min(Math.max((current === -1 ? 0 : current) + direction, 0), tabs.length - 1);
    if (index === current) return;

    this.#activate(index);
    this.#scrollIntoView(tabs[index]);
  }

  /**
   * Scrolls the tablist so a tab is fully visible, centering it when it is not. Scrolls the
   * tablist itself rather than using `scrollIntoView`, which would also move the page vertically.
   * @param {HTMLElement | undefined} tab
   */
  #scrollIntoView(tab) {
    const tablist = this.refs.tablist;
    if (!tab || !tablist) return;

    const list = tablist.getBoundingClientRect();
    const target = tab.getBoundingClientRect();
    if (target.left >= list.left && target.right <= list.right) return;

    tablist.scrollBy({
      left: target.left - list.left - (list.width - target.width) / 2,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /** Disables whichever arrow has nowhere left to go. */
  #syncNav() {
    const { previousButton, nextButton } = this.refs;
    if (!previousButton && !nextButton) return;

    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

    if (previousButton) previousButton.disabled = current <= 0;
    if (nextButton) nextButton.disabled = current === -1 || current >= tabs.length - 1;
  }

  /**
   * Applies a tab's own styling overrides, which — like the label — ride on its panel because the
   * button belongs to the section rather than to the block. Setting them inline on the button beats
   * the section's values, which are declared on the component. A tab that inherits carries no
   * attribute and is left alone, so the section's own value keeps applying.
   */
  #syncTabStyles() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];

    for (const [i, tab] of tabs.entries()) {
      for (const [attribute, [property, unit]] of Object.entries(TAB_STYLE_OVERRIDES)) {
        const value = panels[i]?.getAttribute(attribute);

        if (value == null) tab.style.removeProperty(property);
        else tab.style.setProperty(property, `${value}${unit}`);
      }
    }
  }

  /**
   * Moves the image block a `_tab-image` block renders into its tab button. Like the label, the
   * image has to travel on the panel because a theme block's settings are not readable from the
   * section's Liquid. It is moved rather than copied so the block's `shopify_attributes` stay on a
   * live node and the theme editor can still select the image where it ends up.
   */
  #syncMedia() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const slots = this.refs.tabMedia ?? [];

    for (const [i, slot] of slots.entries()) {
      const incoming = panels[i]?.querySelector('[data-tab-media]')?.firstElementChild;

      if (!incoming) {
        // Nothing waiting: either this tab has no image, or it was lifted on an earlier pass.
        if (!slot.firstElementChild) tabs[i]?.classList.remove('tabs-section__tab--with-media');
        continue;
      }

      slot.replaceChildren(this.#unlink(incoming));
      tabs[i]?.classList.add('tabs-section__tab--with-media');
    }
  }

  /**
   * Returns the node as-is, unless it is a link — the image block renders one when it has a Link
   * set, and an anchor nested in the tab button would be invalid markup and would swallow the
   * click that selects the tab. Rebuilds it as a span, keeping the classes and styles that size it.
   * @param {Element} node
   * @returns {Element}
   */
  #unlink(node) {
    // Moving the node empties its holder; the rebuilt span does not, so take the anchor out by
    // hand. Left behind, it would be picked up as the pending image on the next pass and lifted
    // in — now empty — over the one already in place.
    if (!(node instanceof HTMLAnchorElement)) return node;

    const span = document.createElement('span');
    for (const { name, value } of node.attributes) {
      if (name === 'href' || name === 'target' || name === 'rel') continue;
      span.setAttribute(name, value);
    }
    span.append(...node.childNodes);
    node.remove();
    return span;
  }

  /**
   * Starts a drag when the press lands on a tablist that has somewhere to scroll. Touch is left
   * alone: the strip already scrolls natively there, with momentum and rubber-banding that
   * driving `scrollLeft` by hand would throw away.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    // A press always precedes a click, so this is the point at which a suppression left over
    // from an earlier drag has expired.
    this.#swallowClick = false;
    this.#lastPointerType = event.pointerType;

    const tablist = this.refs.tablist;
    const target = event.target;

    if (event.pointerType === 'touch' || event.button !== 0) return;
    if (!tablist || !(target instanceof Node) || !tablist.contains(target)) return;
    if (tablist.scrollWidth <= tablist.clientWidth) return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: tablist.scrollLeft,
      moved: false,
    };

    // On the window, so a drag that wanders off the strip — or off the page — still tracks.
    window.addEventListener('pointermove', this.#handleDragMove);
    window.addEventListener('pointerup', this.#endDrag);
    window.addEventListener('pointercancel', this.#endDrag);
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    const drag = this.#drag;
    const tablist = this.refs.tablist;
    if (!drag || !tablist || event.pointerId !== drag.pointerId) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      tablist.classList.add('tabs-section__tablist--dragging');
    }

    // Keeps the drag from turning into a text selection across the labels.
    event.preventDefault();
    tablist.scrollLeft = drag.scrollLeft - distance;
  };

  /** @param {PointerEvent} event */
  #endDrag = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    window.removeEventListener('pointermove', this.#handleDragMove);
    window.removeEventListener('pointerup', this.#endDrag);
    window.removeEventListener('pointercancel', this.#endDrag);

    this.refs.tablist?.classList.remove('tabs-section__tablist--dragging');
    // Releasing over a tab fires a click on it; after a real drag that would select a tab the
    // reader was only scrolling past.
    this.#swallowClick = drag.moved;
    this.#drag = null;
  };

  /** @param {Event} event */
  #preventNativeDrag = (event) => {
    const target = event.target;
    if (target instanceof Node && this.refs.tablist?.contains(target)) event.preventDefault();
  };

  /** @param {MouseEvent} event */
  #swallowClickAfterDrag = (event) => {
    if (!this.#swallowClick) return;
    this.#swallowClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    // Enter on a focused tab clicks it with no pointer involved.
    this.#lastPointerType = '';

    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const tabs = this.refs.tabs ?? [];
    const current = tabs.indexOf(target);
    if (current === -1) return;

    // A tablist stacked into a column steps with the up and down arrows as well.
    const tablist = this.refs.tablist;
    const vertical = tablist ? getComputedStyle(tablist).flexDirection.startsWith('column') : false;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowDown':
        if (!vertical) return;
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowUp':
        if (!vertical) return;
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;

    const panels = this.refs.panels ?? [];
    const index = panels.findIndex((panel) => panel === target || panel.contains(target));
    if (index !== -1) this.#activate(index);
  };

  /**
   * Copies each panel's `data-tab-label` into its tab button. Theme blocks
   * can't expose their settings to the section's Liquid, so the label travels
   * on the panel (rendered by the block) and is lifted into the tablist here.
   */
  #syncLabels() {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const labels = this.refs.tabLabels ?? [];

    for (const [i, tab] of tabs.entries()) {
      const label = panels[i]?.getAttribute('data-tab-label');
      if (label == null) continue;

      // Write into the label slot, not the button: the button also holds the image and chevron.
      const target = labels[i] ?? tab;
      if (target.textContent !== label) target.textContent = label;
    }
  }

  /**
   * @param {number} index - The index of the tab to activate.
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, { focus = false } = {}) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    const nextTab = tabs[index];
    if (!nextTab) return;

    // Drop any fade still running, so a quick second click can't let the first
    // one finish and swap in the panel it was headed for.
    this.#fade?.cancel();
    this.#fade = null;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }

    this.#syncNav();

    if (focus) nextTab.focus();

    const outgoing = panels.find((panel) => !panel.hasAttribute('hidden'));
    if (outgoing === panels[index]) return;

    if (!outgoing || !this.#animates) {
      this.#show(index);
      return;
    }

    const half = this.#duration / 2;

    this.#fade = outgoing.animate({ opacity: [1, 0] }, { duration: half, easing: 'ease-in', fill: 'forwards' });
    this.#fade.finished
      .then(() => {
        this.#show(index);
        // The panel is hidden by now, so releasing the held opacity is invisible.
        this.#fade?.cancel();
        this.#fade = null;
        panels[index]?.animate({ opacity: [0, 1] }, { duration: half, easing: 'ease-out' });
      })
      .catch(() => {});
  }

  /**
   * Shows one panel and hides the rest.
   * @param {number} index - The index of the panel to show.
   */
  #show(index) {
    for (const [i, panel] of (this.refs.panels ?? []).entries()) {
      panel.toggleAttribute('hidden', i !== index);
    }

    this.#syncPanelVideos();
  }

  /**
   * Keeps video playback to the panel on screen. Every panel is in the DOM at once, so a video in a
   * closed tab otherwise keeps decoding unseen: several of them compete for the browser's video
   * decoders, and what a shopper gets is a card whose video never starts, or starts late. Pausing
   * what is out of view leaves the decoders to the tab being looked at.
   */
  #syncPanelVideos() {
    for (const panel of this.refs.panels ?? []) {
      const hidden = panel.hasAttribute('hidden');

      for (const video of panel.querySelectorAll('video')) {
        if (!(video instanceof HTMLVideoElement)) continue;

        if (hidden) {
          this.#videoObserver?.unobserve(video);
          this.#suspendVideo(video);
          continue;
        }

        this.#resumeVideo(video);
        // Chrome will not start a muted autoplaying video that is out of view, and nothing retries
        // it — a card below the fold is then left on its poster frame for good. Watching it means
        // the play is tried again the moment it scrolls into view.
        this.#videoObserver?.observe(video);
      }
    }
  }

  /**
   * Pauses a video in a closed panel, marking it as this component's doing so that opening the
   * panel can pick it back up, and so a video the shopper paused themselves is left alone.
   * @param {HTMLVideoElement} video
   */
  #suspendVideo(video) {
    if (video.paused) return;

    video.dataset.tabsPaused = '';
    video.pause();
  }

  /**
   * Restarts a video this component paused, and gives one that autoplays but never got going —
   * its panel was closed, or it was out of view, when the page loaded — its first run. A video the
   * shopper paused stays paused.
   * @param {HTMLVideoElement} video
   */
  #resumeVideo(video) {
    const pausedByTabs = 'tabsPaused' in video.dataset;
    const neverStarted = video.autoplay && video.currentTime === 0;

    if (!pausedByTabs && !neverStarted) return;
    // A slide that isn't the one showing keeps whatever its own card decided for it.
    if (prefersReducedMotion() || video.closest('[hidden]')) return;

    video.play().catch(() => {});
  }

  /**
   * Picks up a video that has scrolled into view in the open panel.
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleVideoVisible = (entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!isIntersecting || !(target instanceof HTMLVideoElement)) continue;

      const panel = (this.refs.panels ?? []).find((candidate) => candidate.contains(target));
      if (panel?.hasAttribute('hidden')) continue;

      this.#resumeVideo(target);
    }
  };

  /**
   * Pauses a video that starts playing while its panel is closed.
   * @param {Event} event
   */
  #pauseHiddenPlayback = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    const panel = (this.refs.panels ?? []).find((candidate) => candidate.contains(video));
    if (!panel) return;

    // Playing again in the open panel: nothing is left to pick back up, so the mark goes here
    // rather than where the play is asked for, which the browser may refuse.
    if (panel.hasAttribute('hidden')) this.#suspendVideo(video);
    else delete video.dataset.tabsPaused;
  };
}

if (!customElements.get('tabs-section-component')) {
  customElements.define('tabs-section-component', TabsSectionComponent);
}
